import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import {
  AbstractControlOptions,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { TypedFormBuilder, TypedFormGroup } from '@gmrappdev/formsafe';
import { AddressInput } from 'src/graphql/generated/graphql.generated';
import { CoordinatesProps } from 'src/store/generated/coordinates-record.generated';

export const emptyAddress: AddressInput = {
  streetAddress: null,
  postalCode: null,
  city: null,
  country: null,
  county: null,
  region: null,
  gpsId: null,
  coordinates: {
    latitude: null,
    longitude: null,
  },
};

export function emptyTypedAddress(formBuilder: TypedFormBuilder) {
  return formBuilder.group<AddressInput>({
    streetAddress: null,
    city: null,
    region: null,
    postalCode: null,
    country: null,
    county: null,
    gpsId: null,
    coordinates: formBuilder.group<CoordinatesProps>({
      latitude: null,
      longitude: null,
    }),
  });
}

export function createAddressFormGroup(
  formBuilder: TypedFormBuilder,
  address: AddressInput | undefined = emptyAddress,
  options: {
    requireCoordinates: boolean;
    requireCityStateZip: boolean;
    controlOptions?: AbstractControlOptions;
  } = {
    requireCoordinates: true,
    requireCityStateZip: false,
  },
): TypedFormGroup<AddressInput> {
  if (address == null) {
    address = Object.assign({}, emptyAddress);
  }

  if (address.coordinates == null) {
    address.coordinates = {
      latitude: null,
      longitude: null,
    };
  }

  return formBuilder.group<AddressInput>(
    {
      streetAddress: [address.streetAddress],
      country: [address.country],
      county: [address.county],
      region: options.requireCityStateZip
        ? [address.region, Validators.required]
        : [address.region],
      city: options.requireCityStateZip ? [address.city, Validators.required] : [address.city],
      postalCode: options.requireCityStateZip
        ? [address.postalCode, Validators.required]
        : [address.postalCode],
      gpsId: [address.gpsId],
      coordinates: formBuilder.group<NonNullable<AddressInput['coordinates']>>({
        latitude: options.requireCoordinates
          ? [address.coordinates.latitude, Validators.required]
          : [address.coordinates.latitude],
        longitude: options.requireCoordinates
          ? [address.coordinates.longitude, Validators.required]
          : [address.coordinates.longitude],
      }),
    },
    options.controlOptions,
  );
}

@Component({
  selector: 'tcs-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressFormComponent implements OnInit {
  @Input()
  public addressCtrl: UntypedFormGroup;

  @Input()
  public outlineStyle = false;

  @Input()
  public latLonConfirmed: boolean | null;

  get gpsIdCtrl() {
    return this.addressCtrl.get('gpsId') as UntypedFormControl;
  }

  @Input()
  public cssClass: any = '';

  ngOnInit(): void {
    if (this.outlineStyle) {
      this.cssClass = 'dense-outline-form-field';
    }
  }
}
